import { useMutation, useQuery } from '@tanstack/react-query'
import type { TableProps } from 'antd'
import type { SorterResult } from 'antd/es/table/interface'
import { notification } from '@surecompanies/core_components'
import { useSearchParams } from 'hooks'
import { useCallback, useEffect, useMemo, useRef, type Key } from 'react'

import { deleteRecruiting, getRecruiting } from '../api'
import {
  RECRUITING_INVITATION_SORT_FIELDS,
  type RecruitingInvitationSortField,
} from '../constants'
import { getErrorMessage } from '../helpers/getErrorMessage'
import {
  resolveInvitationDateFilters,
  resolveInvitationPaginationParams,
  resolveInvitationSortParams,
  resolveInvitationStatusParams,
} from '../helpers/resolveInvitationDateFilters'
import type { RecruitingInvitation, RecruitingResponse } from '../schema'
import { InvitationListFilters } from '../types'

const EMPTY_RECRUITING: RecruitingResponse = {
  data: [],
  total: 0,
  page: 0,
  pageSize: 0,
}

export type RecruitingListQuery = ReturnType<
  typeof resolveInvitationDateFilters
> &
  ReturnType<typeof resolveInvitationSortParams> &
  ReturnType<typeof resolveInvitationStatusParams>

export const recruitingQueryKey = (
  clientPublicKey?: string | null,
  query?: RecruitingListQuery
) =>
  query === undefined
    ? (['recruiting', clientPublicKey] as const)
    : (['recruiting', clientPublicKey, query] as const)

const isServerSortField = (
  field: Key | undefined
): field is RecruitingInvitationSortField =>
  RECRUITING_INVITATION_SORT_FIELDS.includes(
    field as RecruitingInvitationSortField
  )

export function useRecruiting(clientPublicKey?: string | null) {
  const { params, setParams } = useSearchParams<InvitationListFilters>()
  const previousClientPublicKeyRef = useRef<string | null | undefined>(
    undefined
  )

  useEffect(() => {
    const normalizedClient = clientPublicKey ?? null
    const previousClient = previousClientPublicKeyRef.current
    previousClientPublicKeyRef.current = normalizedClient

    if (previousClient === undefined || previousClient === normalizedClient) {
      return
    }

    setParams({ page: '1' }, { replace: true })
  }, [clientPublicKey, setParams])

  const dateFilters = useMemo(
    () => resolveInvitationDateFilters(params),
    [params.benefitEffectiveStartDate, params.benefitEffectiveEndDate]
  )

  const sortParams = useMemo(
    () => resolveInvitationSortParams(params),
    [params.sort, params.order]
  )

  const statusParams = useMemo(
    () => resolveInvitationStatusParams(params),
    [params.status]
  )

  const paginationParams = useMemo(
    () => resolveInvitationPaginationParams(params),
    [params.page, params.pageSize]
  )

  const listQuery = useMemo(
    (): RecruitingListQuery => ({
      ...dateFilters,
      ...sortParams,
      ...statusParams,
      ...paginationParams,
    }),
    [dateFilters, sortParams, statusParams, paginationParams]
  )

  const {
    data,
    isLoading: isLoadingRecruiting,
    refetch: refetchRecruiting,
  } = useQuery({
    queryKey: recruitingQueryKey(clientPublicKey, listQuery),
    queryFn: () =>
      getRecruiting({
        clientPublicKey: clientPublicKey as string,
        ...listQuery,
        page: paginationParams.page ?? 1,
        pageSize: paginationParams.pageSize ?? 10,
      }),
    enabled: !!clientPublicKey,
  })

  const handleTableChange = useCallback<
    NonNullable<TableProps<RecruitingInvitation>['onChange']>
  >(
    (pagination, _filters, sorter) => {
      const nextPage = pagination.current ?? paginationParams.page
      const nextPageSize = pagination.pageSize ?? paginationParams.pageSize

      const paginationChanged =
        nextPage !== paginationParams.page ||
        nextPageSize !== paginationParams.pageSize

      if (paginationChanged) {
        setParams(
          {
            page: String(nextPage),
            pageSize: String(nextPageSize),
          },
          { replace: true }
        )
        return
      }

      const next = (
        Array.isArray(sorter) ? sorter[0] : sorter
      ) as SorterResult<RecruitingInvitation>

      const sortField = (next.columnKey ??
        next.field) as RecruitingInvitationSortField

      if (!isServerSortField(sortField)) {
        return
      }

      if (!next.order) {
        setParams(
          {
            sort: sortField,
            order:
              sortParams.sort === sortField && sortParams.order === 'ASC'
                ? 'DESC'
                : 'ASC',
            page: '1',
          },
          { replace: true }
        )
        return
      }

      setParams(
        {
          sort: sortField,
          order: next.order === 'ascend' ? 'ASC' : 'DESC',
          page: '1',
        },
        { replace: true }
      )
    },
    [
      paginationParams.page,
      paginationParams.pageSize,
      setParams,
      sortParams.order,
      sortParams.sort,
    ]
  )

  const { mutate: cancelRecruiting, isPending: isCancelling } = useMutation({
    mutationFn: (id: string) => deleteRecruiting(id),
    onSuccess: () => {
      notification.success({ message: 'Invitation cancelled' })
      refetchRecruiting()
    },
    onError: (error) => {
      notification.error({
        message: getErrorMessage(
          error,
          'Could not cancel this invitation. Please try again.'
        ),
        duration: 60,
      })
    },
  })

  return {
    recruiting: data ?? EMPTY_RECRUITING,
    isLoadingRecruiting,
    isCancelling,
    cancelRecruiting,
    sort: sortParams.sort,
    order: sortParams.order,
    /** Set only when `sort` is present in the URL (avoids a stuck default sort UI). */
    sortKeyFromUrl: params.sort,
    page: paginationParams.page,
    pageSize: paginationParams.pageSize,
    handleTableChange,
  }
}
